import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowLeftRight, CreditCard as CreditCardIcon, Filter, Loader2, Lock, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ConfirmDialog, Modal } from '@components/Dialog';
import { Pagination } from '@components/Pagination';
import { PermissionGuard } from '@components/PermissionGuard';
import { ActionButton } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import accountService from '@services/accountService';
import categoryService from '@services/categoryService';
import transactionService from '@services/transactionService';
import { getConfig } from '../../config';
import type { Pagination as PaginationInfo } from '@/types/activity';
import {
  SCHEDULE_FILTER_OPTIONS,
  TRANSACTION_KIND_LABELS,
  canToggleSchedule,
  scheduleFilterFromParam,
  type Account,
  type Category,
  type DeleteScope,
  type ScheduleStatusFilter,
  type Transaction,
  type TransactionKind,
  type TransactionSummary,
} from '@/types/finance';
import { addDaysISO, formatDateBR, todayISO } from '@/utils/formatters';
import { formatBRL } from '@/utils/money';
import { currentMonth, monthRange, type DateRange } from '@/utils/period';
import { AmountText, CategorySelect, ColorChip, PeriodPicker, ProjectedBadge, ScheduleBadge, ScheduleToggleButton } from './financeBits';
import { TransactionFormModal } from './TransactionFormModal';

const KINDS: TransactionKind[] = ['all', 'expense', 'income', 'transfer'];

interface Filters {
  range: DateRange;
  account: number | '';
  category: number | '';
  kind: TransactionKind;
  uncategorized: boolean;
  q: string;
  /** Fase 7: filtro "Agendamento" ('' = todos). */
  schedule: ScheduleStatusFilter | '';
}

const defaultFilters = (params?: URLSearchParams): Filters => {
  const m = currentMonth();
  const start = params?.get('start') ?? '';
  const end = params?.get('end') ?? '';
  const schedule = scheduleFilterFromParam(params?.get('agendamento'));
  // Vindo do aviso de agendamento: janela larga (hoje−7 .. hoje+60) para pegar vencidas e próximas.
  const today = todayISO();
  const scheduleRange = schedule ? { start: addDaysISO(today, -7), end: addDaysISO(today, 60) } : null;
  return {
    range: start && end ? { start, end } : scheduleRange ?? monthRange(m.year, m.month),
    account: Number(params?.get('account_id')) > 0 ? Number(params?.get('account_id')) : '',
    category: Number(params?.get('category_id')) > 0 ? Number(params?.get('category_id')) : '',
    kind: (params?.get('kind') as TransactionKind | null) ?? 'all',
    uncategorized: params?.get('uncategorized') === '1',
    q: params?.get('q') ?? '',
    schedule,
  };
};

const SummaryCard: React.FC<{ label: string; cents: number; realized?: number; tone?: 'income' | 'expense' | 'net' }> = ({ label, cents, realized, tone }) => (
  <div className="card p-3">
    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
    <p className={clsx('text-lg font-semibold tabular-nums', tone === 'income' ? 'text-emerald-700' : tone === 'expense' ? 'text-red-600' : cents < 0 ? 'text-red-600' : 'text-slate-800')}>
      {formatBRL(cents)}
    </p>
    {realized !== undefined && (
      <p className="text-small">
        realizado {formatBRL(realized)}
        {realized !== cents && <span className="italic"> · previsto {formatBRL(cents - realized)}</span>}
      </p>
    )}
  </div>
);

/**
 * Finanças → Lançamentos: filtros (período com atalhos de mês, conta, categoria, tipo, sem
 * categoria, busca), resumo do período (GET /transactions/summary), tabela paginada, novo/editar/
 * excluir (parcela: só esta / esta e as seguintes). Fase 7: filtro "Agendamento" (`?agendamento=pendente`)
 * e ação de linha "Marcar como agendada / Desmarcar agendamento".
 */
export const TransactionsView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financas.write').hasPermission;
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>(() => defaultFilters(searchParams));
  const [searchInput, setSearchInput] = useState(filters.q);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Transaction[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [summary, setSummary] = useState<TransactionSummary | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<{ transaction?: Transaction } | null>(null);
  const [deleting, setDeleting] = useState<Transaction | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const pageSize = getConfig().ui.itemsPerPage || 20;

  // Link do sino/painel com a tela já aberta: `?agendamento=` muda sem remontar → reaplica os filtros da URL.
  const scheduleParam = searchParams.get('agendamento');
  useEffect(() => {
    if (!scheduleParam) return;
    setFilters(defaultFilters(searchParams));
    setSearchInput(searchParams.get('q') ?? '');
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleParam]);

  useEffect(() => {
    accountService.getAll(true).then(setAccounts).catch(setError);
    categoryService.getAll({ include_disabled: true }).then(setCategories).catch(setError);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setFilters((f) => (f.q === searchInput.trim() ? f : { ...f, q: searchInput.trim() }));
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!filters.range.start || !filters.range.end) return;
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        transactionService.list({
          account_id: filters.account || undefined,
          category_id: filters.category || undefined,
          uncategorized: filters.uncategorized,
          kind: filters.kind,
          start: filters.range.start,
          end: filters.range.end,
          q: filters.q || undefined,
          schedule_status: filters.schedule || undefined,
          page,
          page_size: pageSize,
        }),
        transactionService.summary(filters.range.start, filters.range.end, filters.account || undefined),
      ]);
      setItems(list.data);
      setPagination(list.pagination);
      setSummary(sum);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const update = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const hasFilters = useMemo(() => {
    const d = defaultFilters();
    return JSON.stringify({ ...d, range: null }) !== JSON.stringify({ ...filters, range: null }) || JSON.stringify(d.range) !== JSON.stringify(filters.range);
  }, [filters]);

  const clearFilters = () => {
    setFilters(defaultFilters());
    setSearchInput('');
    setPage(1);
  };

  const activeAccounts = useMemo(() => accounts.filter((a) => a.is_active !== false), [accounts]);

  const confirmDelete = async (scope: DeleteScope) => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await transactionService.remove(deleting.id, scope);
      setDeleting(null);
      load();
    } catch (err) {
      setDeleting(null);
      setError(err);
    } finally {
      setDeleteBusy(false);
    }
  };

  const isInstallmentGroup = (t: Transaction) => !!t.transaction_group && !!t.repeat_total && t.repeat_total > 1;
  /** Espelho de fatura de cartão: não se edita nem exclui por aqui (409 no backend). */
  const isInvoiceMirror = (t: Transaction) => !!t.credit_card_invoice_id;
  const canEdit = (t: Transaction) => canWrite && !isInvoiceMirror(t);

  /** Troca a linha pelo lançamento devolvido (sem recarregar a página toda). */
  const replaceRow = (updated: Transaction) => setItems((list) => list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));

  const actions = (t: Transaction) =>
    canWrite ? (
      <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
        {canToggleSchedule(t) && <ScheduleToggleButton transaction={t} onDone={replaceRow} onError={setError} />}
        {isInvoiceMirror(t) ? (
          <Link to="/finance/cards" className="p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-50" title="Fatura de cartão: pague ou edite pelo cartão (Finanças → Cartões)">
            <Lock size={16} />
          </Link>
        ) : (
          <>
            <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setForm({ transaction: t })} />
            <ActionButton icon={<Trash2 size={16} />} variant="delete" tooltip="Excluir" onClick={() => setDeleting(t)} />
          </>
        )}
      </div>
    ) : null;

  const descriptionCell = (t: Transaction) => (
    <>
      <span className="text-slate-800 [overflow-wrap:anywhere]">{t.description}</span>
      {t.repeat_total ? <span className="badge-neutral ml-1">{t.repeat_index}/{t.repeat_total}</span> : null}
      {t.transfer_id ? (
        <span className="badge-info ml-1 inline-flex items-center gap-1" title={t.transfer_account_name ? `Transferência · ${t.transfer_account_name}` : 'Transferência'}>
          <ArrowLeftRight size={10} /> Transf.
        </span>
      ) : null}
      {isInvoiceMirror(t) ? (
        <span className="badge-warning ml-1 inline-flex items-center gap-1" title="Pagamento de fatura de cartão: pague ou edite pelo cartão">
          <CreditCardIcon size={10} /> Fatura
        </span>
      ) : null}
      {t.recurring_transaction_id ? (
        <span className="badge-info ml-1" title="Gerado por uma recorrência (ao editar, deixa de ser atualizado por ela; marcar como agendada não desvincula)">
          Recorrente
        </span>
      ) : null}
      {t.is_projected && <ProjectedBadge className="ml-1" />}
      <ScheduleBadge status={t.schedule_status} className="ml-1" />
    </>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <ArrowLeftRight size={20} className="text-slate-500" /> Lançamentos
        </h1>
        <PermissionGuard permission="financas.write">
          <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setForm({})} disabled={activeAccounts.length === 0}>
            Novo lançamento
          </Button>
        </PermissionGuard>
      </div>

      {/* Filtros */}
      <div className="card p-4 space-y-3">
        <PeriodPicker value={filters.range} onChange={(r) => update('range', r)} disabled={loading} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="relative sm:col-span-2">
            <label className="text-label block mb-1">Buscar</label>
            <Search size={16} className="absolute left-3 bottom-2.5 text-slate-400" />
            <input className="input-field pl-9" placeholder="Descrição ou observações…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </div>
          <div>
            <label className="text-label block mb-1">Conta</label>
            <select className="select-field" value={filters.account} onChange={(e) => update('account', e.target.value ? Number(e.target.value) : '')}>
              <option value="">Todas</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.is_active === false ? ' (inativa)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-label block mb-1">Categoria</label>
            <CategorySelect categories={categories} value={filters.category} onChange={(v) => update('category', v)} emptyLabel="Todas" includeDisabled disabled={filters.uncategorized} />
          </div>
          <div>
            <label className="text-label block mb-1">Tipo</label>
            <select className="select-field" value={filters.kind} onChange={(e) => update('kind', e.target.value as TransactionKind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {TRANSACTION_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-label block mb-1">Agendamento</label>
            <select className="select-field" value={filters.schedule} onChange={(e) => update('schedule', e.target.value as ScheduleStatusFilter | '')}>
              {SCHEDULE_FILTER_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>
                  {o.label}
                </option>
              ))}
              {filters.schedule === 'upcoming' && <option value="upcoming">Aguardando</option>}
            </select>
          </div>
          <div className="flex flex-wrap items-end gap-4 sm:col-span-2 lg:col-span-6">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox-field"
                checked={filters.uncategorized}
                onChange={(e) => {
                  update('uncategorized', e.target.checked);
                  if (e.target.checked) update('category', '');
                }}
              />
              Só sem categoria
              {summary && summary.uncategorized_count > 0 && <span className="badge-warning">{summary.uncategorized_count}</span>}
            </label>
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
                <X size={14} /> Limpar filtros
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Resumo do período */}
      {summary && (
        <div className={clsx('grid grid-cols-1 sm:grid-cols-3 gap-3', loading && 'opacity-60')}>
          <SummaryCard label="Receitas" cents={summary.income_cents} realized={summary.realized_income_cents} tone="income" />
          <SummaryCard label="Despesas" cents={summary.expense_cents} realized={summary.realized_expense_cents} tone="expense" />
          <SummaryCard label="Resultado" cents={summary.net_cents} realized={summary.realized_income_cents + summary.realized_expense_cents} tone="net" />
        </div>
      )}

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && items.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!loading && items.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum lançamento no período.</p>}
        {items.map((t) => (
          <div key={t.id} className={clsx('card p-3 space-y-1', loading && 'opacity-60', canEdit(t) && 'cursor-pointer')} onClick={() => canEdit(t) && setForm({ transaction: t })}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm min-w-0">{descriptionCell(t)}</p>
              <AmountText cents={t.amount_cents} projected={t.is_projected} className="text-sm font-medium" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-small">
              <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>{formatDateBR(t.transaction_date)}</span>
                <ColorChip color={t.account_color}>{t.account_name}</ColorChip>
                {t.category_name ? <ColorChip color={t.category_color}>{t.category_name}</ColorChip> : <span className="text-amber-700">sem categoria</span>}
              </span>
              {actions(t)}
            </div>
          </div>
        ))}
        {pagination && <Pagination page={pagination.page} totalPages={pagination.total_pages} totalItems={pagination.total_items} onChange={setPage} />}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="w-28">Data</th>
                <th>Descrição</th>
                <th>Conta</th>
                <th>Categoria</th>
                <th className="text-right">Valor</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center py-12">
                    <Filter size={32} className="text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-600 font-medium">Nenhum lançamento no período</p>
                    <p className="text-small">Ajuste o período ou os filtros.</p>
                  </td>
                </tr>
              )}
              {items.map((t) => (
                <tr key={t.id} className={clsx('table-row', loading && 'opacity-60', canEdit(t) && 'cursor-pointer')} onClick={() => canEdit(t) && setForm({ transaction: t })}>
                  <td className="table-cell whitespace-nowrap">{formatDateBR(t.transaction_date)}</td>
                  <td className="table-cell">{descriptionCell(t)}</td>
                  <td className="table-cell">
                    <ColorChip color={t.account_color}>{t.account_name}</ColorChip>
                  </td>
                  <td className="table-cell">{t.category_name ? <ColorChip color={t.category_color}>{t.category_name}</ColorChip> : <span className="text-amber-700 text-xs">sem categoria</span>}</td>
                  <td className="table-cell text-right">
                    <AmountText cents={t.amount_cents} projected={t.is_projected} className="font-medium" />
                  </td>
                  <td className="table-cell">{actions(t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && <Pagination page={pagination.page} totalPages={pagination.total_pages} totalItems={pagination.total_items} onChange={setPage} />}
      </div>

      {form && (
        <TransactionFormModal
          transaction={form.transaction}
          accounts={activeAccounts}
          categories={categories}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            load();
          }}
          onError={setError}
        />
      )}

      {/* Exclusão: parcela pergunta o escopo; os demais confirmam direto. */}
      {deleting && isInstallmentGroup(deleting) ? (
        <Modal isOpen onClose={() => !deleteBusy && setDeleting(null)} size="sm" closeOnBackdrop={!deleteBusy} showCloseButton={!deleteBusy}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-h3">Excluir parcela</h2>
          </div>
          <div className="px-6 py-4 space-y-2 text-sm text-slate-700">
            <p>
              "{deleting.description}" é a parcela <strong>{deleting.repeat_index}/{deleting.repeat_total}</strong>. O que excluir?
            </p>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg grid grid-cols-1 sm:flex sm:justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)} disabled={deleteBusy}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => confirmDelete('one')} loading={deleteBusy}>
              Só esta
            </Button>
            <Button variant="danger" onClick={() => confirmDelete('future')} loading={deleteBusy}>
              Esta e as seguintes
            </Button>
          </div>
        </Modal>
      ) : (
        <ConfirmDialog
          isOpen={!!deleting}
          onClose={() => setDeleting(null)}
          onConfirm={() => confirmDelete('one')}
          title={deleting?.transfer_id ? 'Excluir transferência' : 'Excluir lançamento'}
          message={
            deleting?.transfer_id
              ? `Excluir a transferência "${deleting.description}"? As duas pernas (origem e destino) são apagadas.`
              : `Excluir "${deleting?.description ?? ''}" de ${deleting ? formatDateBR(deleting.transaction_date) : ''} (${deleting ? formatBRL(deleting.amount_cents) : ''})?`
          }
          confirmText="Excluir"
          severity="danger"
          loading={deleteBusy}
        />
      )}

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
